import {useCallback, useContext, useEffect} from 'react';

import {FlowchartSidepanelManagerContext} from './FlowchartSidepanelUnsavedChangesContext';

/**
 * Allow a tab to opt in to unsaved changes tracking within the Right Sidepanel component.
 *
 * When the tab is dirty, a confirmation modal will be shown when the user tries to close the sidepanel,
 * switch tabs, show another node, or click somewhere else on the canvas.
 *
 * See useFlowchartSidepanelUnsavedChanges for more details on the overall flow.
 *
 * @param tabId - The ID of the tab to register unsaved changes for.
 * @param onDiscard - The callback to call when the user discards the tab.
 * @returns A function to set the dirty state of the tab.
 */
export function useFlowchartSidepanelTabUnsavedChanges(
  tabId: string,
  onDiscard?: () => void | Promise<void>
): {
  setIsTabDirty: (isDirty: boolean) => void;
} {
  const {registerTab, unregisterTab, setIsTabDirty} = useContext(FlowchartSidepanelManagerContext);

  useEffect(() => {
    registerTab({tabId, onDiscard});
  }, [onDiscard, registerTab, tabId]);

  // oxlint-disable-next-line arrow-body-style
  useEffect(() => {
    return () => {
      unregisterTab(tabId);
    };
  }, [unregisterTab, tabId]);

  const setIsTabDirtyWrapper = useCallback(
    (isDirty: boolean) => {
      setIsTabDirty(tabId, isDirty);
    },
    [setIsTabDirty, tabId]
  );

  return {setIsTabDirty: setIsTabDirtyWrapper};
}
